import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import moment from 'moment'
import axios from 'axios'
import { Pencil, Trash2 } from 'lucide-react'
import Loading from '../components/Loading'
import { getRoles } from '../actions/roleActions'
import { GET_ROLES_RESET } from '../constants/roleConstants'

const inputCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const API = process.env.REACT_APP_API_URL;

const Roles = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [data, setData] = useState([])
  const [addRoleOption, setAddRoleOption] = useState(true)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [deletingRole, setDeletingRole] = useState(false)
  const rowsPerPage = 20

  const { userInfo } = useSelector(state => state.userLogin)
  const { getRoleData } = useSelector(state => state.getRoleInfo)
  const canUpdateRoles = !!getRoleData?.rolesUpdate
  const canDeleteRoles = !!getRoleData?.rolesDelete

  useEffect(() => {
    if (getRoleData) {
      setAddRoleOption(getRoleData.rolesAdd)
      if (!getRoleData.rolesView) {
        toast('Not Authorized', { type: 'error', hideProgressBar: true, autoClose: 2000 })
        navigate('/')
      }
    }
  }, [userInfo, getRoleData])

  useEffect(() => { dispatch(getRoles()) }, [])

  const { loadingGetRolesInfo, errorGetRolesInfo, getRolesData } = useSelector(state => state.getRolesInfo)

  useEffect(() => {
    dispatch({ type: GET_ROLES_RESET })
    if (getRolesData) { setLoading(false); setData(getRolesData) }
    else if (errorGetRolesInfo) { setLoading(false); toast(errorGetRolesInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 }) }
  }, [getRolesData, errorGetRolesInfo])

  const handleSearch = e => {
    setSearch(e.target.value)
    setPage(1)
    if (e.target.value) dispatch(getRoles(e.target.value))
    else dispatch(getRoles())
  }

  const filtered = data.filter(r => !search || r.name?.toLowerCase().includes(search.toLowerCase()))
  const totalPages = Math.ceil(filtered.length / rowsPerPage)
  const rows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage)

  const handleDeleteRole = async role => {
    if (!canDeleteRoles) return
    setDeletingRole(true)
    try {
      await axios.delete(`${API}/roles/${role._id}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      })
      setData(current => current.filter(item => item._id !== role._id))
      setRoleToDelete(null)
      toast.success('Role deleted successfully', { hideProgressBar: true, autoClose: 2500 })
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Role deletion failed', {
        hideProgressBar: true,
        autoClose: 3000,
      })
    } finally {
      setDeletingRole(false)
    }
  }

  if (loading) return <Loading />

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Roles</h1>
        {addRoleOption && (
          <Link to="/roles/add" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            + Add Role
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <input
          value={search}
          onChange={handleSearch}
          placeholder="Search roles…"
          className={`${inputCls} w-full max-w-sm`}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Created At</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center text-gray-400 text-sm">No roles found</td>
              </tr>
            ) : rows.map(role => (
              <tr key={role._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  {canUpdateRoles ? (
                    <Link
                      to={`/roles/update/${role._id}`}
                      className="inline-flex rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                    >
                      {role.name}
                    </Link>
                  ) : (
                    <span className="text-sm font-medium text-gray-800">{role.name}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{moment(role.createdAt).format('DD MMM YYYY, HH:mm')}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {canUpdateRoles && (
                      <Link to={`/roles/update/${role._id}`} title="Edit role" aria-label={`Edit ${role.name}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:border-blue-400 hover:bg-blue-50">
                        <Pencil size={15} />
                      </Link>
                    )}
                    {canDeleteRoles && (
                      <button type="button" onClick={() => setRoleToDelete(role)} title="Delete role" aria-label={`Delete ${role.name}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50">
                        <Trash2 size={15} />
                      </button>
                    )}
                    {!canUpdateRoles && !canDeleteRoles && <span className="text-gray-300">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-500">{filtered.length} roles</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                Prev
              </button>
              <span className="text-sm text-gray-600 px-1">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {roleToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-labelledby="delete-role-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600">
              <Trash2 size={21} />
            </div>
            <h2 id="delete-role-title" className="text-lg font-semibold text-gray-900">Delete role?</h2>
            <p className="mt-2 text-sm leading-6 text-gray-500">
              Are you sure you want to delete <span className="font-semibold text-gray-800">{roleToDelete.name}</span>? This action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRoleToDelete(null)}
                disabled={deletingRole}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteRole(roleToDelete)}
                disabled={deletingRole}
                className="inline-flex min-w-[105px] items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
              >
                {deletingRole ? 'Deleting…' : 'Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Roles
